// Mark a digital edition printable, one at a time or by date range.
//
// Separate from publishing, and the separation is the point: an issue can
// be published and readable in the browser while its only PDF is a scan
// nobody can print from. Offering print on one of those sells an order
// nobody can fulfil, which is the failure this flag exists to prevent.
//
// Nothing infers it. Average PDF size per page is flat across every decade
// of the archive, so a scan and a print-ready file look identical from
// here — an operator has to look at the file.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'

function invalidateIssues(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['lab', 'issues'] })
  qc.invalidateQueries({ queryKey: ['lab', 'issues', 'detail'] })
}

export function usePrintReady(editionId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (print_ready: boolean) =>
      api.patch(`/api/lab/issues/${editionId}/print-ready`, { print_ready }),
    onSuccess: () => invalidateIssues(qc),
  })
}

export interface PrintReadyBulk {
  print_ready: boolean
  from_date?: string
  to_date?: string
  publication_slug?: string
}

/** A date range, because that is how an operator knows which PDFs are
 *  print-ready: everything since production changed. The archive is over a
 *  thousand issues and marking a decade by hand does not finish.
 *
 *  Marking ON skips retired issues and any issue with no PDF — there is
 *  nothing to print from. Marking OFF has neither exclusion, so a decade
 *  judged unusable can be withdrawn in one action whatever state it is in. */
export function usePrintReadyBulk() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PrintReadyBulk) =>
      api.post<{ updated: number }>('/api/lab/issues/print-ready-bulk', body),
    onSuccess: () => invalidateIssues(qc),
  })
}

export const PRINT_READY_LABEL: Record<string, string> = {
  yes: 'Printable',
  no: 'Not printable',
}
